<script lang="ts">
/** The unit a discount value is expressed in. */
export type DiscountUnit = 'absolute' | 'percent';
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { ArrowSwapIcon, TextPercentIcon } from '@blpde/nx-icons';
import NxCurrencyInput from './NxCurrencyInput.vue';
import NxNumberInput, { FLOAT_PATTERN } from './NxNumberInput.vue';
import type { InlineIconProps, InputHeight } from './internal';

const props = withDefaults(
  defineProps<{
    /** Currency used while the unit is `absolute`. Defaults to `'EUR'`. */
    currency?: 'EUR';
    /** Height of the field. Defaults to the underlying input's default. */
    height?: InputHeight;
    disabled?: boolean;
    /** Accessible label for the unit-toggle button. */
    toggleLabel?: string;
    /** Toggle tooltip shown while the unit is `absolute`. */
    absoluteTooltip?: string;
    /** Toggle tooltip shown while the unit is `percent`. */
    percentTooltip?: string;
  }>(),
  {
    currency: 'EUR',
    height: undefined,
    disabled: false,
    toggleLabel: 'Switch discount unit',
    absoluteTooltip: 'Absolute amount (€) — click for percentage (%)',
    percentTooltip: 'Percentage (%) — click for absolute amount (€)',
  }
);

/** The decimal value as a string — same representation the underlying
 *  `NxCurrencyInput` / `NxNumberInput` use. */
const value = defineModel<string>({ required: true });
/** Which unit `value` is expressed in. */
const unit = defineModel<DiscountUnit>('unit', { required: true });

function toggleUnit() {
  unit.value = unit.value === 'percent' ? 'absolute' : 'percent';
}

// Leading unit-toggle. It is an action (has onClick) so the wrapper renders it
// as a brand-colored, focusable button with the tooltip on the icon itself.
const swapIcon = computed<InlineIconProps>(() => ({
  component: ArrowSwapIcon,
  align: 'start',
  onClick: toggleUnit,
  label: props.toggleLabel,
  tooltip: unit.value === 'percent' ? props.percentTooltip : props.absoluteTooltip,
}));

// Trailing "%" unit marker for percent mode. In absolute mode the currency input
// contributes its own "€" suffix, so we only add the swap icon there.
const percentSuffix: InlineIconProps = { component: TextPercentIcon, align: 'end' };
</script>

<template>
  <NxCurrencyInput
    v-if="unit === 'absolute'"
    v-model="value"
    :currency="currency"
    :height="height"
    :disabled="disabled"
    :icons="[swapIcon]"
  />
  <NxNumberInput
    v-else
    v-model="value"
    :pattern="FLOAT_PATTERN"
    :min="0"
    :max="100"
    :height="height"
    :disabled="disabled"
    :icons="[swapIcon, percentSuffix]"
  />
</template>
